import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_RICH_PLACEMENT_K_WORDS } from './api/mutation'

import type { Schema } from '../../Sections/KWordsSection/schema'
import type { UseUpdateRichPlacementKWords } from './types'
import type {
  UpdateRichPlacementKWordsMutation,
  UpdateRichPlacementKWordsMutationVariables,
} from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateRichPlacementKWords = (): UseUpdateRichPlacementKWords => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'defaults.richPlacements.kWords.action',
  })
  const { workspaceId } = useWorkspaceParam()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending, mutateAsync } = useMutation<
    { input: UpdateRichPlacementKWordsMutationVariables['input'] },
    UpdateRichPlacementKWordsMutation
  >({
    mutation: UPDATE_RICH_PLACEMENT_K_WORDS,
    onCompleted: async (data: UpdateRichPlacementKWordsMutation): Promise<void> => {
      const apiErrors = data.updateWorkspaceRichPlacementKwordsConfiguration?.errors

      if (apiErrors && apiErrors.length > 0) {
        setErrors(mapGraphQLErrors(getFragmentData(ErrorFragmentFragmentDoc, apiErrors)))
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('success'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['getRichPlacementKWords'],
  })

  const update = (data: Schema): void =>
    void mutateAsync({
      input: {
        ...toDTO(data),
        workspaceId,
      },
    })

  return { errors, isPending, update }
}
